/*!
   Copyright 2019 Ron Buckton

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/
import { isInt, normalizeString, RECURRINGINTERVAL_HASH_SEED } from './internal/core';
import { Temporal } from './types';
import { CivilDate } from './CivilDate';
import { CivilTime } from './CivilTime';
import { CivilDateTime } from './CivilDateTime';
import { OffsetTime } from './OffsetTime';
import { OffsetDateTime } from './OffsetDateTime';
import { ZonedDateTime } from './ZonedDateTime';
import { Duration } from './Duration';
import { Equatable, StructuralEquatable, Equaler } from '@esfx/equatable';

const intervalRegExp = /^R(?<repeat>\d+)?\/(?<second>(?:[^/]|\[[^/]+\])+)\/(?<third>(?:[^/]|\[[^/]+\])+)$/;

export class RecurringInterval<T extends Temporal<T>> implements Equatable, StructuralEquatable {
    readonly repeat: number; // 0 means indefinite
    readonly duration: Duration;
    readonly endpoint: T;
    readonly endpointKind: "start" | "end";
    
    constructor(start: T, duration: Duration, repeat?: number);
    constructor(duration: Duration, end: T, repeat?: number);
    constructor(start: T | Duration, end: T | Duration, repeat: number = 0) {
        if (!isInt(repeat) || repeat < 0) throw new RangeError();
        if (start instanceof Duration) {
            if (end instanceof Duration) throw new TypeError();
            this.endpoint = end;
            this.endpointKind = "end";
            this.duration = start;
            this.repeat = repeat;

            if (this.endpoint.minus(this.duration).compareTo(this.endpoint) > 0) {
                this.endpointKind = "start";
                this.duration = this.duration.negate();
            }
        }
        else {
            if (!(end instanceof Duration)) throw new TypeError();
            this.endpoint = start;
            this.endpointKind = "start";
            this.duration = end;
            this.repeat = repeat;

            if (this.endpoint.plus(this.duration).compareTo(this.endpoint) < 0) {
                this.endpointKind = "end";
                this.duration = this.duration.negate();
            }
        }
    }

    nextOccurrence(value: T) {
        if (this.endpointKind === "start") {
            for (const occurrence of this.occurrences()) {
                if (occurrence[1].compareTo(value) > 0) return occurrence;
            }
        }
        else {
            if (this.endpoint.compareTo(value) <= 0) return undefined;
            let last: [number, T] | undefined;
            for (const occurrence of this.occurrences()) {
                if (occurrence[1].compareTo(value) <= 0) break;
                last = occurrence;
            }
            return last;
        }
    }

    previousOccurrence(value: T) {
        if (this.endpointKind === "end") {
            for (const occurrence of this.occurrences()) {
                if (occurrence[1].compareTo(value) < 0) return occurrence;
            }
        }
        else {
            if (this.endpoint.compareTo(value) >= 0) return undefined;
            let last: [number, T] | undefined;
            for (const occurrence of this.occurrences()) {
                if (occurrence[1].compareTo(value) >= 0) break;
                last = occurrence;
            }
            return last;
        }
    }

    * occurrences(): IterableIterator<[number, T]> {
        let current = this.endpoint;
        for (let i = 0; this.repeat === 0 || i < this.repeat; i++) {
            yield [i, current];
            current = this.endpointKind === "start"
                ? current.plus(this.duration) as T
                : current.minus(this.duration) as T;
        }
    }

    withRepetitions(repeat: number) {
        return this.endpointKind === "start" 
            ? new RecurringInterval(this.endpoint, this.duration, repeat)
            : new RecurringInterval(this.duration, this.endpoint, repeat);
    }

    withStartSameDuration(start: T) {
        return new RecurringInterval(start, this.duration, this.repeat);
    }

    withEndSameDuration(end: T) {
        return new RecurringInterval(this.duration, end, this.repeat);
    }

    withDurationSameEndpoint(duration: Duration) {
        return this.endpointKind === "start" 
            ? new RecurringInterval(this.endpoint, duration, this.repeat)
            : new RecurringInterval(duration, this.endpoint, this.repeat);
    }

    equals(other: RecurringInterval<T>) {
        return this[Equatable.equals](other);
    }

    toString(): string {
        const endpoint = this.endpoint.toISOString();
        const duration = this.duration.toString();
        const first = `R${this.repeat || ""}`;
        const second = this.endpointKind === "start" ? endpoint : duration;
        const third = this.endpointKind === "end" ? endpoint : duration;
        return `${first}/${second}/${third}`;
    }

    toJSON() {
        return this.toString();
    }

    static fromString(text: string) {
        const match = intervalRegExp.exec(normalizeString(text));
        if (match) {
            const { repeat = "", second, third } = match.groups!;
            if (second.startsWith("P")) {
                if (!third.startsWith("P")) {
                    return new RecurringInterval(
                        Duration.fromString(second),
                        parseTemporal(third),
                        +repeat);
                }
            }
            else {
                if (!third.startsWith("P")) throw new SyntaxError();
                return new RecurringInterval(
                    parseTemporal(second),
                    Duration.fromString(third),
                    +repeat);
            }
        }
        throw new SyntaxError(`Invalid recurring interval string '${text}'.`);
    }

    [Equatable.equals](other: unknown): boolean {
        return this[StructuralEquatable.structuralEquals](other, Equaler.defaultEqualer);
    }

    [Equatable.hash](): number {
        return this[StructuralEquatable.structuralHash](Equaler.defaultEqualer);
    }

    [StructuralEquatable.structuralEquals](other: unknown, equaler: Equaler<unknown>): boolean {
        return other instanceof RecurringInterval
            && this.repeat === other.repeat
            && this.endpointKind === other.endpointKind
            && equaler.equals(this.duration, other.duration)
            && equaler.equals(this.endpoint, other.endpoint);
    }

    [StructuralEquatable.structuralHash](equaler: Equaler<unknown>): number {
        let hc = RECURRINGINTERVAL_HASH_SEED;
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this.repeat);
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this.endpointKind);
        hc = ((hc << 7) | (hc >>> 25)) ^ equaler.hash(this.duration);
        hc = ((hc << 7) | (hc >>> 25)) ^ equaler.hash(this.endpoint);
        return hc;
    }
}

function parseTemporal(text: string) {
    try { return OffsetDateTime.fromString(text); } catch { }
    try { return ZonedDateTime.fromString(text); } catch { }
    try { return OffsetTime.fromString(text); } catch { }
    try { return CivilDateTime.fromString(text); } catch { }
    try { return CivilDate.fromString(text); } catch { }
    try { return CivilTime.fromString(text); } catch { }
    throw new SyntaxError(`Invalid temporal string '${text}'.`);
}